<script setup lang="ts">
import PriceCell from './PriceCell.vue'
import PercentageChange from './PercentageChange.vue'
import ListingsBadge from './ListingsBadge.vue'
import { computed } from 'vue'
import { sideConfig } from '@/lib/product'
import type { PriceInfo } from '@/types'

// One market's figures: one country, one currency, one side. The caller renders
// this only when the side is present, so `prices` is never the absent case.
//
// Everything about how the side is worded and coloured comes from `sideConfig`
// rather than from the call site — passing it in had already produced two spellings
// of "Trade-in" on two screens.
const props = defineProps<{
    prices: PriceInfo
    currencySymbol: string
    side: 'resell' | 'buyback'
}>()

const config = computed(() => sideConfig[props.side])

// Buyback has no marketplace seller behind the vendor, so there is nothing to put
// on the first line.
const showReseller = computed(() => props.side === 'resell')
</script>

<template>
    <div class="flex items-center">
        <div class="w-24 shrink-0 font-bold text-xs uppercase text-left pr-4" :class="config.accent.text">
            {{ config.label }}
        </div>

        <div class="w-24 shrink-0">
            <PriceCell :value="prices.min_price" :currency-symbol="currencySymbol" />
        </div>
        <div class="w-24 shrink-0">
            <PriceCell :value="prices.max_price" :currency-symbol="currencySymbol" />
        </div>
        <div class="w-24 shrink-0">
            <PriceCell :value="prices.trend_avg_price" :currency-symbol="currencySymbol" />
        </div>

        <div class="w-32 shrink-0 flex items-center gap-1">
            <PercentageChange :value="prices.last30_days_price_change" />
            <span class="text-xs text-muted-foreground/40">/</span>
            <PercentageChange :value="prices.last60_days_price_change" />
        </div>

        <div class="w-20 shrink-0 pl-3" :class="config.accent.text">
            <ListingsBadge :value="prices.online_items" plain />
        </div>

        <div class="w-40 shrink-0 min-w-0">
            <div v-if="showReseller" class="truncate text-xs text-foreground">
                {{ prices.top_competitor?.reseller }}
            </div>
            <div class="truncate text-xs text-muted-foreground">
                {{ prices.top_competitor?.vendor }}
            </div>
        </div>
    </div>
</template>
